import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { ReactTyped } from "react-typed";
import { login } from "../../services/api";
import ThemeContext from "../../ThemeContext";
import { URL_SEGMENTS } from "../../../config/Routesconfig";
import "./Login.css";
import logo from "../../../Assets/LOGO-GENYDMS(2).png";

const BACKEND         = process.env.REACT_APP_BACKEND_URL || "";
const MAX_RECENT      = 5;
const STORAGE_KEY     = "onefincore_recent_logins";

// ── Helpers to read / write recent logins from localStorage ──
const getRecentLogins = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
  } catch {
    return [];
  }
};

const saveRecentLogin = (username, password, role) => {
  try {
    const existing = getRecentLogins();
    const filtered = existing.filter(u => u.username !== username);
    const updated = [{ username, password, role }, ...filtered].slice(0, MAX_RECENT);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch {
    // silently ignore storage errors
  }
};

const removeRecentLogin = (username) => {
  try {
    const existing = getRecentLogins();
    const updated  = existing.filter(u => u.username !== username);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch {}
};

const BADGE_STYLE = {
  superadmin:        { bg: "#cfe2ff", color: "#084298", label: "Superadmin"   },
  reseller:          { bg: "#d1e7dd", color: "#0a3622", label: "Reseller"     },
  client_admin:      { bg: "#fff3cd", color: "#664d03", label: "Client Admin"  },
  enterprise_client: { bg: "#f8d7da", color: "#842029", label: "Enterprise"   },
  admin:             { bg: "#e2e3e5", color: "#41464b", label: "Admin"        },
};

const Login = () => {
  const [username, setUsername]                   = useState("");
  const [password, setPassword]                   = useState("");
  const [usernameValidated, setUsernameValidated] = useState(false);
  const [registeredEmail, setRegisteredEmail]     = useState("");
  const [error, setError]                         = useState("");
  const [showPopup, setShowPopup]                 = useState(false);
  const [showPassword, setShowPassword]           = useState(false);
  const [loading, setLoading]                     = useState(false);

  // ── Recent logins state ──
  const [recentLogins, setRecentLogins] = useState([]);

  // ── Shake state ──
  const [shakeError, setShakeError] = useState(false);

  const navigate     = useNavigate();
  const { setTheme } = useContext(ThemeContext);

  // ── Load recent logins from localStorage on mount ──
  useEffect(() => {
    setRecentLogins(getRecentLogins());
  }, []);

  // ── Auto-clear shake after animation ──
  useEffect(() => {
    if (!shakeError) return;
    const t = setTimeout(() => setShakeError(false), 620);
    return () => clearTimeout(t);
  }, [shakeError]);

  useEffect(() => {
    window.history.replaceState({ reset: true }, "", "/login");
    window.history.pushState({ reset: true }, "", "/login");

    const handlePopState = () => {
      if (window.location.pathname === "/login") {
        setUsername("");
        setPassword("");
        setUsernameValidated(false);
        setRegisteredEmail("");
        setError("");
        setShowPopup(false);
        setShowPassword(false);
        setLoading(false);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const triggerPopup = (msg) => {
    setError(msg);
    setShowPopup(true);
    setShakeError(true);
  };

  const validateUsernameFromBackend = async () => {
    const trimmed = username.trim();

    if (!trimmed) {
      triggerPopup("Username is required. Please enter your username.");
      return;
    }
    if (/\s/.test(username)) {
      triggerPopup("Username cannot contain spaces.");
      return;
    }
    if (!/^[a-zA-Z0-9._@-]+$/.test(trimmed)) {
      triggerPopup(
        "Username contains invalid characters. Only letters, numbers, dots (.), underscores (_), @ and hyphens (-) are allowed."
      );
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${BACKEND}/api/webmanager/validate-username`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ username: trimmed }),
      });

      if (response.ok) {
        try {
          const emailRes = await fetch(`${BACKEND}/api/webmanager/get-email-by-username`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ username: trimmed }),
          });
          if (emailRes.ok) {
            const emailData = await emailRes.json();
            setRegisteredEmail(emailData.email || "");
          }
        } catch {
          setRegisteredEmail("");
        }

        setUsernameValidated(true);
        setError("");
        setShowPopup(false);
      } else if (response.status === 403) {
        triggerPopup("Your account has been deactivated. Please contact your administrator.");
      } else {
        triggerPopup("Record not found or user deactivated.");
      }
    } catch {
      triggerPopup("Unable to connect to the server. Please check your internet connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    if (!password.trim()) {
      triggerPopup("Password is required. Please enter your password.");
      return;
    }
    if (/\s/.test(password)) {
      triggerPopup("Password cannot contain spaces.");
      return;
    }

    setLoading(true);
    try {
      const response = await login({ username: username.trim(), password });
      const { token, user, theme } = response.data;

      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(user));
      setTheme(theme);
      localStorage.setItem(`theme_${user.id || user.username}`, JSON.stringify(theme));

      // ── Save this successful login to recent logins ──
      saveRecentLogin(user.username, password, user.role);
      setRecentLogins(getRecentLogins());

      if (user.role === "reseller") {
  navigate(`/${user.username}/${URL_SEGMENTS.clients}`, { replace: true });
} else if (user.role === "superadmin") {
  navigate(`/${URL_SEGMENTS.superadmin}/${URL_SEGMENTS.resellers}`, { replace: true });
} else if (user.role === "admin") {
  navigate(`/${URL_SEGMENTS.admin}/${URL_SEGMENTS.dashboard}`, { replace: true });
} else if (user.role === "client_user") {
  navigate(`/${user.username}/user-dashboard`, { replace: true });
} else {
  navigate(`/${user.username}/${URL_SEGMENTS.dashboard}`, { replace: true });
}
    } catch (err) {
      const status = err?.response?.status;
      setPassword("");

      if (status === 403) {
        triggerPopup("Your account has been deactivated. Please contact your administrator.");
      } else if (status === 429) {
        triggerPopup("Too many failed login attempts. Please wait a moment and try again.");
      } else {
        triggerPopup("The username and/or password you entered is incorrect.");
      }
    } finally {
      setLoading(false);
    }
  };

  const goToForgotPassword = () => {
    navigate("/forgot-password", {
      state: {
        username:          username.trim(),
        prefilled_email:   registeredEmail,
        username_verified: true,
      },
    });
  };

  // ── Click a recent login card — auto-fill username + password ──
  const handleRecentFill = (recentUser, recentPass) => {
    setUsername(recentUser);
    setPassword(recentPass);
    setUsernameValidated(false);
    setError("");
    setShowPopup(false);
  };

  // ── Remove a single saved login ──
  const handleRemoveRecent = (e, recentUser) => {
    e.stopPropagation();
    removeRecentLogin(recentUser);
    setRecentLogins(getRecentLogins());
  };

  // ── Clear all saved logins ──
  const handleClearAll = () => {
    localStorage.removeItem(STORAGE_KEY);
    setRecentLogins([]);
  };

  return (
    <div className="container-fluid p-0 login-container">
      <div className="row g-0 min-vh-100">

        {/* LEFT PANEL */}
        <div className="col-md-6 d-flex flex-column justify-content-center align-items-center text-white left-panel">
          <h1 className="welcome-heading">
            Welcome to{" "}
            <ReactTyped
              strings={["OneFinCore"]}
              typeSpeed={60}
              backSpeed={40}
              backDelay={500}
              showCursor={false}
            />
          </h1>
          <div />
          <h1 className="welcome-heading">
            Smart. Scalable. Built for <span>modern financial institutions</span>
          </h1>
          <div>
            <p>
              Accelerate sales, stay compliant, and deliver exceptional customer
              <br /> experiences – with AI-driven efficiency and control.
            </p>
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="col-md-6 d-flex justify-content-center align-items-center right-panel">
          <div className="card shadow border-0 login-card">

            <div
              className="card-header text-center text-white"
              style={{ backgroundColor: "#001F54" }}
            >
              Welcome Member!
            </div>

            <div className="card-body px-4 py-3">

              {/* LOGO */}
              <div className="logo-wrapper mx-auto mb-3">
                <img src={logo} alt="Logo" className="logo-img" />
              </div>

              {/* STEP 1 — Username */}
              {!usernameValidated ? (
                <>
                  <label className="form-label fw-bold mb-1">USERNAME:</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Insert Your Username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value.replace(/\s/g, ""))}
                    onKeyDown={(e) => e.key === "Enter" && !loading && validateUsernameFromBackend()}
                    autoFocus
                    autoComplete="username"
                    disabled={loading}
                  />

                  <div className="text-end mt-1">
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-decoration-none"
                      style={{ color: "#001F54", fontSize: "0.82rem" }}
                      onClick={() => navigate("/forgot-username")}
                      disabled={loading}
                    >
                      Forgot Username?
                    </button>
                  </div>

                  <button
                    className="btn btn-sm w-100 mt-2 text-white d-flex align-items-center justify-content-center gap-2"
                    style={{ backgroundColor: "#001F54" }}
                    onClick={validateUsernameFromBackend}
                    disabled={loading}
                    type="button"
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" />
                        Verifying...
                      </>
                    ) : (
                      "Next"
                    )}
                  </button>
                </>
              ) : (
                /* STEP 2 — Password */
                <>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <label className="form-label fw-bold mb-0">USERNAME:</label>
                    <span
                      className="text-primary change-link"
                      style={{ cursor: "pointer", fontSize: "0.82rem" }}
                      onClick={() => {
                        if (!loading) {
                          setUsernameValidated(false);
                          setRegisteredEmail("");
                          setPassword("");
                          setShowPassword(false);
                          setError("");
                          setShowPopup(false);
                        }
                      }}
                    >
                      Change
                    </span>
                  </div>
                  <input
                    type="text"
                    className="form-control form-control-sm mb-3"
                    value={username}
                    disabled
                  />

                  <label className="form-label fw-bold mb-1">PASSWORD:</label>
                  <div className="d-flex gap-2">
                    <input
                      type={showPassword ? "text" : "password"}
                      className="form-control form-control-sm"
                      placeholder="Insert Your Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value.replace(/\s/g, ""))}
                      onKeyDown={(e) => e.key === "Enter" && !loading && handleLogin()}
                      autoFocus
                      autoComplete="current-password"
                      disabled={loading}
                    />
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={loading}
                      type="button"
                    >
                      {showPassword ? "Hide" : "Show"}
                    </button>
                  </div>

                  <button
                    className="btn btn-sm text-white w-100 mt-3 d-flex align-items-center justify-content-center gap-2"
                    style={{ backgroundColor: "#4CAF50" }}
                    onClick={handleLogin}
                    disabled={loading}
                    type="button"
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" />
                        Signing in...
                      </>
                    ) : (
                      "Login"
                    )}
                  </button>

                  <div className="text-center mt-2">
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-decoration-none"
                      style={{ color: "#001F54", fontSize: "0.82rem" }}
                      onClick={goToForgotPassword}
                      disabled={loading}
                    >
                      Forgot Password?
                    </button>
                  </div>
                </>
              )}

              {/* ── RECENT LOGINS — always-visible inline panel ── */}
              <div className="recent-logins-section">
                <div className="recent-logins-header">
                  <div className="recent-logins-title-row">
                    <div className="recent-logins-icon">
                      <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                        <circle cx="8" cy="8" r="7.5" stroke="#001F54" strokeWidth="1.2" />
                        <circle cx="8" cy="5" r="1" fill="#001F54" />
                        <rect x="7.2" y="7" width="1.6" height="5" rx="0.8" fill="#001F54" />
                      </svg>
                    </div>
                    <span className="recent-logins-count-label">
                      {recentLogins.length > 0
                        ? `${recentLogins.length} recent login${recentLogins.length > 1 ? "s" : ""} — click any to fill`
                        : "No recent logins"}
                    </span>
                  </div>
                  {recentLogins.length > 0 && (
                    <button type="button" className="recent-logins-clear-btn" onClick={handleClearAll}>
                      Clear all
                    </button>
                  )}
                </div>

                {recentLogins.length > 0 && (
                  <div className="recent-logins-list">
                    {recentLogins.map(({ username: rUser, role: rRole, password: rPass }) => {
                      const b = BADGE_STYLE[rRole] || { bg: "#e2e3e5", color: "#41464b", label: rRole };
                      return (
                        <div
                          key={rUser}
                          className="recent-login-row"
                          onClick={() => handleRecentFill(rUser, rPass)}
                        >
                          <div className="recent-login-left">
                            <div className="recent-login-avatar" style={{ background: b.bg, color: b.color }}>
                              {rUser.charAt(0).toUpperCase()}
                            </div>
                            <div className="recent-login-info">
                              <span className="recent-login-username">{rUser}</span>
                              <span className="recent-login-badge" style={{ background: b.bg, color: b.color }}>
                                {b.label}
                              </span>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="recent-login-remove"
                            title="Remove"
                            onClick={(e) => handleRemoveRecent(e, rUser)}
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {/* ── END RECENT LOGINS ── */}

            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="footer text-white py-3">
        <div className="ps-5">
          © 2026 <span className="fw-bold">OneFinCore</span> All Rights Reserved.
        </div>
      </footer>

      {/* ── IMPROVED ERROR POPUP ── */}
      {showPopup && (
        <div className="popup-overlay" onClick={() => { setShowPopup(false); setError(""); }}>
          <div
            className={`popup-box${shakeError ? " popup-shake" : ""}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Icon row */}
            <div className="popup-icon-row">
              <div className="popup-icon-circle">
                <span>⚠</span>
              </div>
            </div>

            {/* Title */}
            <h5 className="popup-title">Error</h5>

            {/* Styled message */}
            <div className="popup-message-box">
              <p className="popup-message-text">{error}</p>
            </div>

            {/* Close button */}
            <button
              className="btn popup-close-btn w-100"
              onClick={() => { setShowPopup(false); setError(""); }}
              type="button"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;